// Utility to fetch ENSO (El Niño/La Niña) status from NOAA
// This fetches the latest Oceanic Niño Index (ONI) and interprets phase, strength, and trend.
// Optionally, you can add a sparkline for Niño 3.4 index trend.

// NOAA ONI CSV: https://www.cpc.ncep.noaa.gov/data/indices/oni.ascii.txt
// Docs: https://origin.cpc.ncep.noaa.gov/products/analysis_monitoring/ensostuff/ONI_v5.php

export interface ENSOStatus {
  phase: "El Niño" | "La Niña" | "Neutral";
  strength: "Weak" | "Moderate" | "Strong";
  trend: "strengthening" | "weakening" | "stable";
  impact: string;
  updated: string;
  nino34: number[]; // Niño 3.4 index values (recent)
  currentValue: number;
}

// Helper to determine phase and strength from ONI value
function interpretENSO(oni: number): {
  phase: ENSOStatus["phase"];
  strength: ENSOStatus["strength"];
} {
  if (oni >= 0.5) {
    if (oni >= 1.5) return { phase: "El Niño", strength: "Strong" };
    if (oni >= 1.0) return { phase: "El Niño", strength: "Moderate" };
    return { phase: "El Niño", strength: "Weak" };
  }
  if (oni <= -0.5) {
    if (oni <= -1.5) return { phase: "La Niña", strength: "Strong" };
    if (oni <= -1.0) return { phase: "La Niña", strength: "Moderate" };
    return { phase: "La Niña", strength: "Weak" };
  }
  return { phase: "Neutral", strength: "Weak" };
}

// Helper to determine trend
function getTrend(recent: number[]): ENSOStatus["trend"] {
  if (recent.length < 2) return "stable";
  const diff = recent[recent.length - 1] - recent[recent.length - 2];
  if (Math.abs(diff) < 0.1) return "stable";
  return diff > 0 ? "strengthening" : "weakening";
}

type Region = {
  name: string;
  bounds: {
    latMin: number;
    latMax: number;
    lonMin: number;
    lonMax: number;
  };
};

// Order = priority (most specific first)
const REGIONS: Region[] = [
  // --- AFRICA (detailed) ---
  {
    name: "East Africa",
    bounds: { latMin: -15, latMax: 15, lonMin: 28, lonMax: 52 },
  },
  {
    name: "West Africa",
    bounds: { latMin: 0, latMax: 20, lonMin: -20, lonMax: 15 },
  },
  {
    name: "Central Africa",
    bounds: { latMin: -10, latMax: 10, lonMin: 10, lonMax: 30 },
  },
  {
    name: "Southern Africa",
    bounds: { latMin: -35, latMax: -10, lonMin: 10, lonMax: 40 },
  },
  {
    name: "North Africa",
    bounds: { latMin: 15, latMax: 37, lonMin: -20, lonMax: 35 },
  },

  // --- MIDDLE EAST ---
  {
    name: "Middle East",
    bounds: { latMin: 12, latMax: 40, lonMin: 35, lonMax: 65 },
  },

  // --- SOUTH ASIA ---
  {
    name: "South Asia",
    bounds: { latMin: 5, latMax: 35, lonMin: 65, lonMax: 90 },
  },

  // --- SOUTHEAST ASIA ---
  {
    name: "Southeast Asia",
    bounds: { latMin: -10, latMax: 25, lonMin: 90, lonMax: 140 },
  },

  // --- EAST ASIA ---
  {
    name: "East Asia",
    bounds: { latMin: 20, latMax: 50, lonMin: 100, lonMax: 145 },
  },

  // --- CENTRAL ASIA ---
  {
    name: "Central Asia",
    bounds: { latMin: 35, latMax: 55, lonMin: 60, lonMax: 90 },
  },

  // --- EUROPE ---
  {
    name: "Western Europe",
    bounds: { latMin: 40, latMax: 60, lonMin: -10, lonMax: 10 },
  },
  {
    name: "Eastern Europe",
    bounds: { latMin: 40, latMax: 65, lonMin: 10, lonMax: 40 },
  },
  {
    name: "Northern Europe",
    bounds: { latMin: 60, latMax: 75, lonMin: -25, lonMax: 40 },
  },
  {
    name: "Southern Europe",
    bounds: { latMin: 35, latMax: 45, lonMin: -10, lonMax: 40 },
  },

  // --- NORTH AMERICA ---
  {
    name: "Caribbean",
    bounds: { latMin: 10, latMax: 25, lonMin: -90, lonMax: -60 },
  },
  {
    name: "Central America",
    bounds: { latMin: 5, latMax: 20, lonMin: -95, lonMax: -75 },
  },
  {
    name: "United States",
    bounds: { latMin: 25, latMax: 49, lonMin: -125, lonMax: -66 },
  },
  {
    name: "Canada",
    bounds: { latMin: 49, latMax: 75, lonMin: -140, lonMax: -50 },
  },

  // --- SOUTH AMERICA ---
  {
    name: "Northern South America",
    bounds: { latMin: 0, latMax: 12, lonMin: -80, lonMax: -50 },
  },
  {
    name: "Amazon Basin",
    bounds: { latMin: -15, latMax: 5, lonMin: -75, lonMax: -50 },
  },
  {
    name: "Southern South America",
    bounds: { latMin: -55, latMax: -15, lonMin: -80, lonMax: -50 },
  },

  // --- OCEANIA ---
  {
    name: "Australia",
    bounds: { latMin: -45, latMax: -10, lonMin: 110, lonMax: 155 },
  },
  {
    name: "Melanesia",
    bounds: { latMin: -20, latMax: 0, lonMin: 140, lonMax: 170 },
  },
  {
    name: "Polynesia",
    bounds: { latMin: -30, latMax: 10, lonMin: 170, lonMax: -120 },
  },

  // --- ENSO CORE REGION ---
  {
    name: "Equatorial Pacific (Niño 3.4)",
    bounds: { latMin: -5, latMax: 5, lonMin: -170, lonMax: -120 },
  },

  // --- CONTINENT FALLBACKS ---
  {
    name: "Africa",
    bounds: { latMin: -35, latMax: 37, lonMin: -20, lonMax: 55 },
  },
  {
    name: "Asia",
    bounds: { latMin: -10, latMax: 55, lonMin: 60, lonMax: 150 },
  },
  {
    name: "Europe",
    bounds: { latMin: 35, latMax: 75, lonMin: -25, lonMax: 40 },
  },
  {
    name: "North America",
    bounds: { latMin: 5, latMax: 75, lonMin: -170, lonMax: -50 },
  },
  {
    name: "South America",
    bounds: { latMin: -60, latMax: 15, lonMin: -85, lonMax: -30 },
  },
];

const isValidCoord = (val?: number | null) =>
  typeof val === "number" && !Number.isNaN(val);

const inBounds = (
  lat: number,
  lon: number,
  { latMin, latMax, lonMin, lonMax }: Region["bounds"],
): boolean => {
  // Latitude check (simple)
  const inLat = lat >= latMin && lat <= latMax;
  if (!inLat) return false;

  // Longitude check (handle antimeridian)
  if (lonMin <= lonMax) {
    // Normal case: e.g., -10 to 10
    return lon >= lonMin && lon <= lonMax;
  } else {
    // Antimeridian case: e.g., 170 to -120 (spans 180°)
    return lon >= lonMin || lon <= lonMax;
  }
};

export const getRegionFromCoords = (
  latitude?: number | null,
  longitude?: number | null,
): string => {
  if (!isValidCoord(latitude) || !isValidCoord(longitude)) {
    return "Unknown region";
  }

  for (const region of REGIONS) {
    if (inBounds(latitude!, longitude!, region.bounds)) {
      return region.name;
    }
  }

  return "Global";
};

// Example regional impact (could be improved with more logic)
function getImpact(
  phase: ENSOStatus["phase"],
  latitude?: number | null,
  longitude?: number | null,
): string {
  const region = getRegionFromCoords(latitude, longitude);

  if (phase === "El Niño") return `↓ Drier in ${region}`;
  if (phase === "La Niña") return `↑ Increased rainfall in ${region}`;
  return `Mixed regional impacts for ${region}`;
}

export async function fetchENSOStatus(opts?: {
  latitude?: number | null;
  longitude?: number | null;
}): Promise<ENSOStatus> {
  let text: string | null = null;
  let lastError: Error | null = null;

  // Try endpoints in the order that works for local dev and production.
  const endpoints = [
    "/api/enso-proxy", // Vite dev proxy / Netlify API route
    "/.netlify/functions/api/enso-proxy", // Netlify redirect path
    "/.netlify/functions/enso-proxy", // fallback if deployed to old Netlify function path
    "https://www.cpc.ncep.noaa.gov/data/indices/oni.ascii.txt", // direct fallback if proxy not available
  ];

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint);
      if (!res.ok) {
        lastError = new Error(
          `Proxy fetch failed: ${res.status} ${res.statusText}`,
        );
        console.warn(`ENSO proxy request failed for ${endpoint}:`, lastError);
        // Try next candidate for 404; for all errors we continue to potentially hit fallback.
        continue;
      }

      const contentType = (res.headers.get("content-type") || "").toLowerCase();
      const candidateText = await res.text();

      // Guard against HTML fallback from SPA redirects (e.g., Netlify "/*" redirection to index.html)
      if (
        contentType.includes("html") ||
        candidateText.trim().startsWith("<!DOCTYPE html") ||
        candidateText.includes("<html")
      ) {
        lastError = new Error(
          `ENSO proxy returned non-CSV content from ${endpoint}`,
        );
        console.warn(
          `ENSO proxy non-data response for ${endpoint}:`,
          lastError,
        );
        continue;
      }

      text = candidateText;
      break;
    } catch (error) {
      lastError = error as Error;
      console.warn(`ENSO proxy request error for ${endpoint}:`, lastError);
      continue;
    }
  }

  if (!text) {
    throw new Error(
      `ENSO proxy unavailable. Last error: ${lastError?.message || "unknown"}`,
    );
  }

  // Parse the last 12 ONI values (Niño 3.4 index)
  const lines = text.trim().split("\n");
  const oniValues = lines
    .map((line) => {
      const parts = line.trim().split(/\s+/);
      return parseFloat(parts[2]);
    })
    .filter((v) => !isNaN(v));
  const nino34 = oniValues.slice(-12); // Last 12 values (3 years)

  if (nino34.length === 0) {
    throw new Error("ENSO data parse failed: no ONI values parsed");
  }

  const currentValue = nino34[nino34.length - 1];
  if (Number.isNaN(currentValue)) {
    throw new Error("ENSO data parse failed: current ONI value is NaN");
  }

  const { phase, strength } = interpretENSO(currentValue);
  const trend = getTrend(nino34);
  const impact = getImpact(phase, opts?.latitude, opts?.longitude);
  const updated = `Updated ${new Date().toLocaleString()}`;
  return { phase, strength, trend, impact, updated, nino34, currentValue };
}
